import type { AIStore } from '..';
import type { ProjectContextValue } from '@core/context';
import type useLocalStorage from '@core/hooks/useLocalStorage';
import type { UIMessage } from 'ai';
import type { Draft } from 'immer';
import type { StateCreator } from 'zustand';

import { fetcher } from '@core/hooks/useReadmeApi';
import { actionLog } from '@core/store/util';
import { validateFile } from '@routes/SuperHub/AI/Chat/ChatEditor/utils/attachment';

import { cacheKey, ChatListStorage, ChatMessagesStorage, failSilently } from './storage';

export interface ChatModel {
  enabled: boolean;
  id: string;
  name: string;
  provider: string;
}

interface MessageMetadata {
  createdAt: Date | string;
  onboardingChecklist?: {
    cards?: {
      aiTab?: 'configuration' | 'dropdown' | 'linter' | 'mcp';
      completed?: boolean;
      description: string;
      href?: string;
      title: string;
      type: 'feature' | 'setting';
    }[];
    formattedLinkMessage?: string;
    formattedLinks?: { text: string; url: string }[];
    textEntries?: string[];
  };
  syntheticType?: SyntheticMessageType;
}

export type SyntheticMessageType = 'onboarding-checklist';

export type MessageWithMetadata = UIMessage & {
  metadata: MessageMetadata;
};

interface Checkpoint {
  action: string;
  content: Record<string, unknown> | string;
  messages: MessageWithMetadata[];
  timestamp: Date;
}

interface AgentContext {
  /**
   * Hidden context is not shown to the user, but included in the prompt
   */
  hiddenContext?: Record<string, string>;
  message?: string;
  prompt?: string;
  runImmediately?: boolean;
  title?: string;
  type?: string;
}

export type AttachmentUploadStatus = 'error' | 'pending' | 'uploaded' | 'uploading';

export interface PendingAttachment {
  abortController?: AbortController;
  error?: string;
  file: File;
  id: string;
  previewUrl: string;
  uploadedKey?: string;
  uploadStatus: AttachmentUploadStatus;
}

export interface ChatInfo {
  createdAt: string;
  id: string;
  lastMessage?: string;
  messageCount: number;
  title: string;
}

interface AiAgentSliceState {
  attachments: PendingAttachment[];
  chatId: string | null;
  chats: ChatInfo[];
  checkpoints: Checkpoint[];
  /**
   * Context for pre-populating chat input when navigating from other components.
   */
  context?: AgentContext;
  /**
   * Indicates whether the agent is currently initializing
   */
  isInitializing: boolean;
  model: ChatModel | null;
  /**
   * All models and their enabled state. This should only be initialized in SuperHub. Currently safe since this slice is only used in SuperHub. Should be moved to SuperHub store if this feature get released outside of SuperHub.
   */
  modelsSetting: ChatModel[];
  /**
   * Flag to indicate messages should be reset
   */
  resetFlag: boolean;
  storage?: ReturnType<typeof useLocalStorage>;
  /**
   * Base user key for persisting chats (subdomain-userId without timestamp)
   */
  userKey: string;
}

interface AiAgentSliceAction {
  addAttachments: (files: File[] | FileList) => void;
  /**
   * Adds a new chat to the list
   * @param title - The chat title
   * @param prefix - Optional ID prefix, defaults to 'chat'
   * @returns The generated chat ID, or null if reusing an existing empty chat
   */
  addChat: (title: string, prefix?: string) => string | null;
  addCheckpoint: (checkpoint: {
    action: string;
    content: Record<string, unknown> | string;
    messages: MessageWithMetadata[];
  }) => number;
  clearAttachments: () => void;
  /**
   * Clears context after it has been consumed.
   */
  clearContext: () => void;
  /**
   * Clears the reset flag after useChat has processed it
   */
  clearResetFlag: () => void;
  /**
   * Closes a chat by removing it from the list
   */
  closeChat: (chatId: string) => void;
  completeUpload: (id: string, key: string) => void;
  failUpload: (id: string, error: string) => void;
  initialize: (params: {
    chatId: string;
    project: {
      // cannot use type ProjectContextValue['project']['ai']['chat']['models'] here because chat is optional
      models: ChatModel[];
      subdomain: ProjectContextValue['project']['subdomain'];
    };
    storage: ReturnType<typeof useLocalStorage>;
  }) => void;
  popCheckpoint: () => void;
  removeAttachment: (id: string) => void;
  /**
   * Renames a chat
   * @returns true if the rename was successful, false if validation failed or title unchanged
   */
  renameChat: (chatId: string, newTitle: string) => boolean;
  reset: () => void;
  /**
   * Resets messages in storage
   */
  resetMessages: (setMessages?: (messages: MessageWithMetadata[]) => void) => void;
  /**
   * Restores attachments from a saved array (e.g., for error recovery).
   * Creates new preview URLs since the old ones were revoked.
   */
  restoreAttachments: (attachments: PendingAttachment[]) => void;
  retryUpload: (id: string) => void;
  saveModels: (params: { models: ChatModel[]; subdomain: string }) => Promise<ChatModel[]>;
  /**
   * Sets AI context for pre-populating chat input.
   */
  setContext: (context: AgentContext) => void;
  setModel: (model: ChatModel | null) => void;
  startUpload: (id: string) => void;
  /**
   * Switches to a different chat
   */
  switchToChat: (chatId: string) => void;
  updateChatId: (chatId: string, messageCount: number) => void;
  /**
   * Updates chat info (title, last message, etc.)
   */
  updateChatInfo: (chatId: string, updates: Partial<Omit<ChatInfo, 'id'>>) => void;
}

export interface AiAgentSlice {
  agent: AiAgentSliceAction & AiAgentSliceState;
}

export const ATTACHMENT_LIMIT = 5;
const MAX_FILE_SIZE_BYTES = 18 * 1024 * 1024;

const generateAttachmentId = () => {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `attachment-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const MAX_TITLE_LENGTH = 50;
const NEW_CHAT_TITLE = 'New Chat';

const initialState: AiAgentSliceState = {
  attachments: [],
  chatId: null,
  chats: [],
  checkpoints: [],
  context: {},
  isInitializing: true,
  model: null,
  modelsSetting: [],
  resetFlag: false,
  storage: undefined,
  userKey: '',
};

export const createAiAgentSlice: StateCreator<
  AIStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  AiAgentSlice
> = (set, get) => {
  const persistChats = () => {
    const state = get().agent;
    if (state.userKey) {
      ChatListStorage.save(state.userKey, state.chats);
    }
  };

  const removeEmptyDefaultChatIfNeeded = (state: Draft<AiAgentSliceState>, excludeChatId?: string) => {
    if (!state.chatId) return;

    const currentActiveChat = state.chats.find(c => c.id === state.chatId);
    if (!currentActiveChat) return;

    if (excludeChatId && currentActiveChat.id === excludeChatId) return;

    if (currentActiveChat.messageCount === 0 && currentActiveChat.title === NEW_CHAT_TITLE) {
      const chatIndex = state.chats.findIndex(c => c.id === currentActiveChat.id);
      if (chatIndex !== -1) {
        state.chats.splice(chatIndex, 1);
        ChatMessagesStorage.remove(currentActiveChat.id);
      }
    }
  };

  return {
    agent: {
      ...initialState,

      addAttachments: files => {
        const allFiles = Array.from(files);
        const oversizedFiles = allFiles.filter(file => file.size > MAX_FILE_SIZE_BYTES);
        const supportedFiles = allFiles.filter(file => validateFile(file) !== null && file.size <= MAX_FILE_SIZE_BYTES);
        const unsupportedFiles = allFiles.filter(file => !validateFile(file));

        if (unsupportedFiles.length > 0) {
          throw new Error(`Unsupported media: ${unsupportedFiles[0].name}`);
        }
        if (oversizedFiles.length > 0) {
          throw new Error(`File exceeds 18MB limit: ${oversizedFiles[0].name}`);
        }
        if (supportedFiles.length === 0) {
          throw new Error('No files uploaded');
        }

        set(
          state => {
            const currentAttachments = state.agent.attachments;
            const availableSlots = ATTACHMENT_LIMIT - currentAttachments.length;
            if (availableSlots <= 0) {
              throw new Error('Attachment limit reached');
            }

            const newAttachments = supportedFiles.slice(0, availableSlots).map(file => ({
              id: generateAttachmentId(),
              file,
              previewUrl: URL.createObjectURL(file),
              uploadStatus: 'pending' as const,
              abortController: new AbortController(),
            }));

            state.agent.attachments.push(...newAttachments);
          },
          false,
          actionLog('ai.agent.addAttachments', { count: supportedFiles.length }),
        );
      },

      addChat: (title: string, prefix: string = 'hubAgent') => {
        const currentState = get().agent;

        // Check for existing empty default chat
        const emptyDefaultChat = currentState.chats.find(
          chat => chat.messageCount === 0 && chat.title === NEW_CHAT_TITLE,
        );

        if (emptyDefaultChat) {
          set(
            state => {
              state.agent.chatId = emptyDefaultChat.id;
            },
            false,
            actionLog('ai.agent.addChat.reuseEmpty', { chatId: emptyDefaultChat.id }),
          );
          return null;
        }

        const chatId = `${prefix}-${currentState.userKey}-${Date.now()}`;
        const newChat: ChatInfo = {
          id: chatId,
          title,
          createdAt: new Date().toISOString(),
          messageCount: 0,
        };

        set(
          state => {
            if (state.agent.chats.length >= 5) {
              const sortedByCreatedAt = [...state.agent.chats].sort(
                (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
              );
              const oldestChat = sortedByCreatedAt[0];
              const oldestIndex = state.agent.chats.findIndex(c => c.id === oldestChat.id);
              if (oldestIndex !== -1) {
                state.agent.chats.splice(oldestIndex, 1);
                ChatMessagesStorage.remove(oldestChat.id);
              }
            }

            state.agent.chats.push(newChat);
            state.agent.chatId = chatId;
          },
          false,
          actionLog('ai.agent.addChat', { chatId }),
        );

        persistChats();

        return chatId;
      },

      addCheckpoint: checkpoint => {
        const newCheckpoint: Checkpoint = {
          ...checkpoint,
          timestamp: new Date(),
        };

        set(
          (state: any) => {
            state.agent.checkpoints.push(newCheckpoint);
          },
          false,
          actionLog('ai.agent.addCheckpoint', newCheckpoint),
        );

        return get().agent.checkpoints.length;
      },

      clearAttachments: () => {
        get().agent.attachments.forEach(attachment => {
          if (attachment.abortController) {
            attachment.abortController.abort();
          }
        });

        set(
          state => {
            state.agent.attachments.forEach(attachment => {
              URL.revokeObjectURL(attachment.previewUrl);
            });
            state.agent.attachments = [];
          },
          false,
          actionLog('ai.agent.clearAttachments'),
        );
      },

      clearContext: () => {
        set(
          state => {
            state.agent.context = {};
          },
          false,
          actionLog('ai.agent.clearContext'),
        );
      },

      clearResetFlag: () => {
        set(
          state => {
            state.agent.resetFlag = false;
          },
          false,
          actionLog('ai.agent.clearResetFlag'),
        );
      },

      closeChat: (chatIdToClose: string) => {
        ChatMessagesStorage.remove(chatIdToClose);

        set(
          state => {
            const chatIndex = state.agent.chats.findIndex(c => c.id === chatIdToClose);
            if (chatIndex === -1) return;

            const isActiveChat = state.agent.chatId === chatIdToClose;
            state.agent.chats.splice(chatIndex, 1);

            if (isActiveChat && state.agent.chats.length > 0) {
              state.agent.chatId = state.agent.chats[state.agent.chats.length - 1].id;
            } else if (state.agent.chats.length === 0) {
              state.agent.chatId = null;
            }
          },
          false,
          actionLog('ai.agent.closeChat', { chatId: chatIdToClose }),
        );

        persistChats();
      },

      completeUpload: (id, key) => {
        set(
          state => {
            const attachment = state.agent.attachments.find(a => a.id === id);
            if (attachment) {
              attachment.uploadStatus = 'uploaded';
              attachment.uploadedKey = key;
              attachment.error = undefined;
            }
          },
          false,
          actionLog('ai.agent.completeUpload', { id, key }),
        );
      },

      failUpload: (id, error) => {
        set(
          state => {
            const attachment = state.agent.attachments.find(a => a.id === id);
            if (attachment) {
              attachment.uploadStatus = 'error';
              attachment.error = error;
            }
          },
          false,
          actionLog('ai.agent.failUpload', { id, error }),
        );
      },

      initialize: async ({ chatId, storage, project }) => {
        try {
          const response = await fetcher<{ data: { models: ChatModel[] } }>(
            `/${project.subdomain}/api-next/v2/chat/models/setting`,
            {
              method: 'GET',
            },
          );
          const models = response.data.models;
          const storedModel = storage.getItem('model') as ChatModel | null;
          const enabledModels = models.filter(m => m.enabled);
          const model =
            storedModel && enabledModels.find(m => m.id === storedModel.id) ? storedModel : enabledModels[0] || null;
          const existingContext = get().agent.context;
          const persistedChats = ChatListStorage.get(chatId);

          set(
            state => {
              if (state.agent.chats.length === 0) {
                Object.assign(state.agent, {
                  ...initialState,
                  chatId,
                  context:
                    existingContext && Object.keys(existingContext).length > 0 ? existingContext : initialState.context,
                });

                if (persistedChats.length > 0) {
                  state.agent.chats = persistedChats;
                  state.agent.chatId = persistedChats[persistedChats.length - 1].id;
                } else {
                  state.agent.chats.push({
                    id: chatId,
                    title: NEW_CHAT_TITLE,
                    messageCount: 0,
                    createdAt: new Date().toISOString(),
                  });
                }
              }

              if (state.agent.chats.length > 0 && !state.agent.chatId) {
                state.agent.chatId = state.agent.chats[state.agent.chats.length - 1].id;
              }

              state.agent.storage = storage;
              state.agent.userKey = chatId;
              state.agent.modelsSetting = models;
              state.agent.model = model;
              state.agent.isInitializing = false;
            },
            false,
            actionLog('ai.agent.initialize', { chatId, modelsCount: models.length }),
          );
        } catch (error) {
          const storedModel = storage.getItem('model') as ChatModel | null;
          const enabledProjectModels = project.models.filter(m => m.enabled);
          const model =
            storedModel && enabledProjectModels.find(m => m.id === storedModel.id)
              ? storedModel
              : enabledProjectModels[0] || null;

          set(
            state => {
              state.agent.modelsSetting = project.models;
              state.agent.model = model;
              state.agent.isInitializing = false;
            },
            false,
            actionLog('ai.agent.initialize.models.error', {
              message: error instanceof Error ? error.message : String(error),
            }),
          );
        }
      },

      popCheckpoint: () => {
        set(
          state => {
            state.agent.checkpoints.pop();
          },
          false,
          actionLog('ai.agent.popCheckpoint'),
        );
      },

      removeAttachment: id => {
        // Abort any in-flight upload before removing
        const attachment = get().agent.attachments.find(a => a.id === id);
        if (attachment?.abortController) {
          attachment.abortController.abort();
        }

        set(
          state => {
            const att = state.agent.attachments.find(a => a.id === id);
            if (att) {
              URL.revokeObjectURL(att.previewUrl);
              state.agent.attachments = state.agent.attachments.filter(a => a.id !== id);
            }
          },
          false,
          actionLog('ai.agent.removeAttachment', { id }),
        );
      },

      renameChat: (chatId: string, newTitle: string) => {
        const chat = get().agent.chats.find(c => c.id === chatId);
        if (!chat) {
          return false;
        }

        const validatedTitle = newTitle.length > MAX_TITLE_LENGTH ? newTitle.slice(0, MAX_TITLE_LENGTH) : newTitle;

        if (validatedTitle === chat.title) {
          return false;
        }

        set(
          state => {
            const chatToUpdate = state.agent.chats.find(c => c.id === chatId);
            if (chatToUpdate) {
              chatToUpdate.title = validatedTitle;
            }
          },
          false,
          actionLog('ai.agent.renameChat', { chatId, newTitle: validatedTitle }),
        );

        persistChats();
        return true;
      },

      reset: () => {
        get().agent.clearAttachments();

        set(
          state => {
            Object.assign(state.agent, initialState);
          },
          false,
          actionLog('ai.agent.reset'),
        );
      },

      resetMessages: setMessages => {
        const { chatId, storage, userKey, chats } = get().agent;
        if (!chatId || !storage) return;

        ChatMessagesStorage.remove(chatId);
        failSilently(() => storage.removeItem(cacheKey(chatId)));

        get().agent.clearAttachments();

        const newChatId = `hubAgent-${userKey}-${Date.now()}`;

        const chatIndex = chats.findIndex(c => c.id === chatId);

        set(
          state => {
            if (chatIndex !== -1) {
              state.agent.chats[chatIndex] = {
                ...state.agent.chats[chatIndex],
                id: newChatId,
                messageCount: 0,
                lastMessage: undefined,
                title: NEW_CHAT_TITLE,
                createdAt: new Date().toISOString(),
              };
            }
            state.agent.chatId = newChatId;
            state.agent.resetFlag = true;
          },
          false,
          actionLog('ai.agent.resetMessages', { oldChatId: chatId, newChatId }),
        );

        persistChats();

        if (setMessages) {
          setMessages([]);
        }
      },

      restoreAttachments: savedAttachments => {
        set(
          state => {
            const restoredAttachments = savedAttachments.map(att => ({
              ...att,
              previewUrl: URL.createObjectURL(att.file),
              abortController: undefined,
            }));
            state.agent.attachments = restoredAttachments;
          },
          false,
          actionLog('ai.agent.restoreAttachments', { count: savedAttachments.length }),
        );
      },

      retryUpload: id => {
        set(
          state => {
            const attachment = state.agent.attachments.find(a => a.id === id);
            if (attachment) {
              attachment.uploadStatus = 'pending';
              attachment.error = undefined;
              attachment.abortController = new AbortController();
            }
          },
          false,
          actionLog('ai.agent.retryUpload', { id }),
        );
      },

      saveModels: async ({ models, subdomain }) => {
        const apiUrl = `/${subdomain}/api-next/v2/chat/models/setting`;
        const requestBody = JSON.stringify({ models });

        const response = await fetcher<{ data: { models: ChatModel[] } }>(apiUrl, {
          method: 'POST',
          body: requestBody,
        });

        const savedModels = response.data.models;

        set(
          state => {
            state.agent.modelsSetting = savedModels;
          },
          false,
          actionLog('ai.agent.saveModels', { count: savedModels.length }),
        );

        return savedModels;
      },

      setContext: context => {
        set(
          state => {
            state.agent.context = context;
          },
          false,
          actionLog('ai.agent.setContext', { context }),
        );
      },

      setModel: model => {
        set(
          state => {
            state.agent.model = model;
          },
          false,
          actionLog('ai.agent.setModel', { model }),
        );
        const { storage } = get().agent;
        if (!storage) return;

        failSilently(() => storage.setItem('model', model));
      },

      startUpload: id => {
        set(
          state => {
            const attachment = state.agent.attachments.find(a => a.id === id);
            if (attachment) {
              attachment.uploadStatus = 'uploading';
            }
          },
          false,
          actionLog('ai.agent.startUpload', { id }),
        );
      },

      switchToChat: (chatId: string) => {
        set(
          state => {
            removeEmptyDefaultChatIfNeeded(state.agent, chatId);

            const targetChat = state.agent.chats.find(c => c.id === chatId);
            if (targetChat) {
              state.agent.chatId = chatId;
            }
          },
          false,
          actionLog('ai.agent.switchToChat', { chatId }),
        );

        persistChats();
      },

      updateChatId: (chatId: string, messageCount: number) => {
        set(
          state => {
            state.agent.chatId = chatId;

            const chat = state.agent.chats.find(c => c.id === chatId);
            if (chat) {
              chat.messageCount = messageCount;
            }
          },
          false,
          actionLog('ai.agent.updateChatId', { chatId, messageCount }),
        );

        persistChats();
      },

      updateChatInfo: (chatId: string, updates: Partial<Omit<ChatInfo, 'id'>>) => {
        set(
          state => {
            const chat = state.agent.chats.find(c => c.id === chatId);
            if (chat) {
              Object.entries(updates).forEach(([key, value]) => {
                if (value !== undefined) {
                  (chat as Record<string, unknown>)[key] = value;
                }
              });
            }
          },
          false,
          actionLog('ai.agent.updateChatInfo', { chatId, updates }),
        );

        persistChats();
      },
    },
  };
};
